/** @prettier */

/**
 * VAT番号設定ダイアログ
 */

import {useState, useEffect} from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
import {VATNumberSelectBox} from '../../component/SelectBox';
//API import
import {
  getBillingCheckout,
  postBillingSettingVATNumber,
} from '../../API/API_Collection';
//Style import
import '../../css/common/common.css';
import {format, pullDownMenu, billing} from '../../define';
import {vatNumberFormatType} from '../../Type/TypeDef';

/**
 * VAT番号設定ダイアログ
 * @param props
 * - gwID ゲートウェイID
 * - isOpenDialog ダイアログの開閉
 * - setIsOpenDialog ダイアログの開閉セット関数
 * @returns JSX
 */
const SettingVATNumberDialog = (props: {
  gwID: string;
  isOpenDialog: boolean;
  setIsOpenDialog: (arg0: boolean) => void;
}) => {
  /** VATの書式 */
  const [vatFormat, setVATFormat] = useState<vatNumberFormatType>('');
  const [vatNumber, setVATNumber] = useState(''); //VAT番号(文字列)
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenAPIErrorDialog, setIsOpenAPIErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [vatNumberError, setVATNumberError] = useState(false); //クーポンコード

  const trans = useTranslation().t; //翻訳関数
  const {siteID} = useParams();

  //入力項目のチェック
  useEffect(() => {
    if (vatNumber === '' || vatNumberError || apiWait) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [vatNumber, vatNumberError, apiWait]);

  /**
   * OKボタン押下時処理
   */
  const settingVATNumber = () => {
    //パスパラメータを取得できない場合
    if (props.gwID === undefined || siteID === undefined) {
      return;
    }
    //VAT番号が取得できない場合
    if (vatNumber === undefined) {
      return;
    }
    //VATフォーマットが未選択の場合
    if (vatFormat === '') {
      return;
    }

    setAPIWait(true);

    // VAT番号を設定
    postBillingSettingVATNumber(siteID, props.gwID, {
      'vat-format': vatFormat,
      'vat-number': vatNumber,
    })
      .then(response => {
        // VAT番号の設定に成功した場合
        if (response.result === 'OK') {
          // チェックアウトに遷移
          getBillingCheckout(siteID, props.gwID)
            .then(response => {
              // ダイアログを閉じる
              props.setIsOpenDialog(false);
              window.location.href = response.url;
            })
            .catch(err => {
              if (err.response.data.code === 'UNREGISTERED') {
                alert(trans('SettingVATNumberDialog.alertMsg.unregistered'));
              } else if (err.response.data.code === 'COUNTRY_NOT_LIST') {
                alert(trans('SettingVATNumberDialog.alertMsg.notlist'));
              } else {
                setIsOpenAPIErrorDialog(true);
              }
              setAPIWait(false);
            });
          // VAT番号の設定に失敗した場合
        } else {
          alert(trans('SettingVATNumberDialog.alertMsg.vatnumber'));
          setAPIWait(false);
        }
      })
      .catch(() => {
        setIsOpenAPIErrorDialog(true);
        setAPIWait(false);
      });
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
  };

  //パスパラメータを取得できない場合
  if (siteID === undefined) return <></>;

  return (
    <>
      <Dialog open={props.isOpenDialog} onClose={handleClose}>
        <DialogTitle>{trans('SettingVATNumberDialog.title')}</DialogTitle>
        <DialogContent>
          {/* ダイアログに表示するメッセージ */}
          <DialogContentText>
            {trans('SettingVATNumberDialog.description')}
          </DialogContentText>
          <Stack spacing={2}>
            <Grid container columnSpacing={1.5} rowSpacing={2}>
              <Grid
                item
                xs={12}
                className={'billingTable__data__setVATNumberDialog__subTitle'}>
                {trans('SettingVATNumberDialog.subTitle')}
              </Grid>
              <Grid item xs={6}>
                {/* VAT番号のフォーマット */}
                <VATNumberSelectBox
                  choices={pullDownMenu.VAT_NUMBER_FORMAT}
                  value={vatFormat}
                  setValue={setVATFormat}
                  class={'billingTable__data__setVATNumberDialog__selectBox'}
                />
              </Grid>
              <Grid item xs={6}>
                {/* VAT番号 */}
                <ChkInputTextbox
                  setValue={setVATNumber}
                  setError={setVATNumberError}
                  chkLength={format.BASIC_LENGTH_MAX}
                  type="text"
                  value={vatNumber}
                  id="mv-inputApplyCouponCode"
                  class="billingTable__data__setVATNumberDialog__textField"
                  helperText=" "
                  errHelperText={trans(
                    'SettingVATNumberDialog.txtField.errHelperTxt'
                  )}
                  variant="standard"
                  // formatに応じてプレースホルダーを変化させる
                  placeholder={billing.vatPlaceholderTable[vatFormat]}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          {/* APPLY/CANCELボタンの設定） */}
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('SettingVATNumberDialog.buttonTxt.cancel')}
          />
          <PrimaryButton
            onClick={() => settingVATNumber()}
            buttonText={trans('SettingVATNumberDialog.buttonTxt.apply')}
            disabled={okBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      {/* エラー表示 */}
      <APIErrorMessage
        open={isOpenAPIErrorDialog}
        setOpen={setIsOpenAPIErrorDialog}
      />
    </>
  );
};
export default SettingVATNumberDialog;
